import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Copy, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntegrationPanel() {
  const [settings, setSettings] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [freshToken, setFreshToken] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/webhook`, { withCredentials: true });
      setSettings(data);
    } catch {
      toast.error("Could not load integration settings.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rotate = async () => {
    setRotating(true);
    try {
      const { data } = await axios.post(`${API}/admin/webhook/rotate`, {}, { withCredentials: true });
      setFreshToken(data.token);
      setConfirmRotate(false);
      setRevealed(true);
      load();
    } catch {
      toast.error("Rotation failed.");
    } finally {
      setRotating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied."));
  };

  if (!settings) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-secondary-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="integration-panel">
      <div className="border border-border p-8 md:p-10">
        <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-2">Outrank — Blog auto-publish</p>
        <h3 className="font-serif text-2xl font-light tracking-tight mb-8">Webhook integration</h3>

        <div className="space-y-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-2">Webhook endpoint</p>
            <div className="flex items-center gap-3 border border-border px-4 py-3 bg-card/60">
              <code className="text-xs font-light truncate flex-1" data-testid="webhook-endpoint">
                {`${process.env.REACT_APP_BACKEND_URL}${settings.endpoint}`}
              </code>
              <button
                onClick={() => copy(`${process.env.REACT_APP_BACKEND_URL}${settings.endpoint}`)}
                data-testid="webhook-endpoint-copy"
                className="text-secondary-foreground hover:text-foreground transition-colors shrink-0"
                aria-label="Copy endpoint"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-2">Access token</p>
            <div className="flex items-center gap-3 border border-border px-4 py-3 bg-card/60">
              <code className="text-xs font-light truncate flex-1" data-testid="webhook-token">
                {revealed ? (freshToken || settings.token) : "••••••••••••••••••••••••••••"}
              </code>
              <button
                onClick={() => setRevealed(!revealed)}
                data-testid="webhook-token-reveal"
                className="text-secondary-foreground hover:text-foreground transition-colors shrink-0"
                aria-label={revealed ? "Hide token" : "Reveal token"}
              >
                {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => copy(freshToken || settings.token)}
                data-testid="webhook-token-copy"
                className="text-secondary-foreground hover:text-foreground transition-colors shrink-0"
                aria-label="Copy token"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {settings.rotated_at && (
              <p className="mt-2 text-xs font-light text-muted-foreground">
                Last rotated: {new Date(settings.rotated_at).toLocaleString()}
              </p>
            )}
          </div>

          {freshToken && (
            <p className="text-xs font-light leading-relaxed text-destructive border border-destructive/40 px-4 py-3" data-testid="rotate-warning">
              New token generated. Paste it into your Outrank webhook settings now — the previous token has
              stopped working.
            </p>
          )}

          <Button
            onClick={() => setConfirmRotate(true)}
            data-testid="webhook-rotate-btn"
            className="rounded-none bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-8 py-5"
          >
            <KeyRound className="w-4 h-4 mr-2" /> Rotate token
          </Button>
        </div>
      </div>

      <Dialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <DialogContent className="max-w-md rounded-none" data-testid="rotate-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light tracking-tight">Rotate access token?</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-light text-muted-foreground leading-relaxed">
            A new token will be generated and the current one will stop working immediately. You will need to
            paste the new token into your Outrank webhook settings to keep auto-publishing running.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmRotate(false)}
              className="rounded-none text-xs tracking-[0.2em] uppercase"
            >
              Cancel
            </Button>
            <Button
              onClick={rotate}
              disabled={rotating}
              data-testid="rotate-confirm-btn"
              className="rounded-none bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase"
            >
              {rotating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rotate now"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
